import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Auth } from '../services/auth';
import { ImpersonationService } from '../services/impersonation.service';

// Helper function to safely access localStorage (only in browser environment)
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value);
    }
  },
  removeItem: (key: string): void => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(key);
    }
  },
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(Auth);
  const impersonationService = inject(ImpersonationService);
  const accessToken = safeLocalStorage.getItem('accessToken');
  const refreshToken = safeLocalStorage.getItem('refreshToken');

  const routesToSkip = [
    '/agb',
    '/privacy',
    '/imprint',
    '/contact',
    '/verify-email',
    '/login',
    '/signup',
    '/reset-password',
  ];

  // Skip auth handling if logout is in progress
  if (authService.getIsLoggingOut()) {
    return next(req);
  }

  // Skip auth handling for logout endpoint
  if (req.url.includes('auth/logout')) {
    return next(req);
  }

  // Clone the request and add the auth header if we have a token
  if (accessToken) {
    let headers = req.headers.set('Authorization', `Bearer ${accessToken}`);
    const impersonateId = impersonationService.impersonatedUserId();
    if (impersonateId != null) {
      headers = headers.set('X-Impersonate-User-Id', String(impersonateId));
    }
    req = req.clone({ headers });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // only log in local environment
        if (!environment.production) {
          console.debug(error, {
            refreshToken,
            accessToken,
            routerUrl: router.url,
          });
        }
      }

      // If we get a 401 and have a refresh token, try to refresh
      if (
        error.status === 401 &&
        refreshToken &&
        !authService.getIsLoggingOut()
      ) {
        return authService.refreshToken(refreshToken).pipe(
          switchMap((response: any) => {
            safeLocalStorage.setItem(
              'accessToken',
              response.data.tokens.accessToken,
            );
            safeLocalStorage.setItem(
              'refreshToken',
              response.data.tokens.refreshToken,
            );
            return next(
              req.clone({
                headers: req.headers.set(
                  'Authorization',
                  `Bearer ${response.data.tokens.accessToken}`,
                ),
              }),
            );
          }),
          catchError((refreshError) => {
            // If refresh fails, clear tokens and redirect to login
            if (!authService.getIsLoggingOut()) {
              safeLocalStorage.removeItem('accessToken');
              safeLocalStorage.removeItem('refreshToken');
              router.navigate(['/signin']);
            }
            return throwError(() => refreshError);
          }),
        );
      }

      // If we get a 401 and don't have a refresh token, redirect to login
      if (error.status === 401 && !authService.getIsLoggingOut()) {
        safeLocalStorage.removeItem('accessToken');
        safeLocalStorage.removeItem('refreshToken');
        router.navigate(['/signin']);
      }

      // Skip auth handling on verify-email page
      const urlParts = router.url.split('?');
      const path = urlParts[0];
      if (routesToSkip.includes(path) || router.url === '/') {
        return throwError(() => error);
      }

      return throwError(() => error);
    }),
  );
};
